import {
  DecisionAction,
  DecisionActionCompact,
  DecisionActivity,
  DecisionActivityActor,
  DecisionActivityImpact,
  DecisionDetailItem,
  DecisionDocumentRef,
  DecisionEvidence,
  DecisionListItem,
  DecisionMissingInformation,
  DecisionOwner,
  DecisionQuery,
  DecisionSourceTrace,
  DecisionSummary,
  HomeIaActionInputProperty,
  HomeIaActionInputSchema,
} from "../../../models/home";
import {
  HomeActivityActorResponse,
  HomeActivityImpactResponse,
  HomeActivityNarrativeResponse,
  HomeDecisionDocumentRefResponse,
  HomeDecisionFiltersResponse,
  HomeDecisionItemResponse,
  HomeDecisionListItemResponse,
  HomeDecisionOwnerResponse,
  HomeDecisionSourceTraceResponse,
  HomeDecisionSummaryResponse,
  HomeEvidenceResponse,
  HomeIaActionInputPropertyResponse,
  HomeIaActionInputSchemaResponse,
  HomeMissingInformationResponse,
  HomeNextBestActionCompactResponse,
  HomeNextBestActionResponse,
} from "../../../models/home/dtos";
import {
  toOptionalState,
  toConfidenceCompactViewModel,
  toConfidenceViewModel,
  toOptionalExpectedState,
  toOptionalActionHttpMethod,
  toPriorityCompactViewModel,
  toPriorityViewModel,
  toRiskCompactViewModel,
  toRiskViewModel,
  toState
} from "./decision.adapter.status-mappers";

export function toCompactActionViewModel(
    dto?: HomeNextBestActionCompactResponse | null
  ): DecisionActionCompact | undefined {
    if (!resolveActionKey(dto) && !resolveActionLabel(dto)) {
      return undefined;
    }

    if (!dto) {
      return undefined;
    }

    return {
      actionKey: resolveActionKey(dto) ?? "",
      label: resolveActionLabel(dto) ?? "",
      source: dto.source ?? undefined,
      requiresUserAction: dto.requiresUserAction ?? false,
      deadline: dto.deadline ?? undefined,
      enabled: dto.enabled ?? false,
      disabledReason: dto.disabledReason ?? undefined,
      kind: dto.kind ?? undefined,
      placement: dto.placement ?? undefined,
      requiresInput: dto.requiresInput ?? false,
      confirmationRequired: dto.confirmationRequired ?? false,
      expectedState: toOptionalExpectedState(dto.expectedState),
      refreshHints: dto.refreshHints ?? [],
      riskLevel: dto.riskLevel ?? undefined,
      executionMode: dto.executionMode ?? undefined,
      endpointTemplate: dto.endpointTemplate ?? undefined,
      httpMethod: toOptionalActionHttpMethod(dto.httpMethod),
      payloadTemplate: dto.payloadTemplate ?? undefined
    };
  }

export function toCompactAvailableActionsViewModel(
    dto?: readonly HomeNextBestActionCompactResponse[] | null
  ): readonly DecisionActionCompact[] | undefined {
    if (!dto) {
      return undefined;
    }

    return dto
      .map((action) => toCompactActionViewModel(action))
      .filter((action): action is DecisionActionCompact => !!action);
  }

export function toActionViewModel(dto?: HomeNextBestActionResponse | null): DecisionAction | null {
    if (!resolveActionKey(dto) && !resolveActionLabel(dto) && !dto?.description) {
      return null;
    }

    if (!dto) {
      return null;
    }

    return {
      actionKey: resolveActionKey(dto) ?? "",
      label: resolveActionLabel(dto) ?? "",
      description: dto.description ?? undefined,
      source: dto.source ?? undefined,
      sourceConfidence: dto.sourceConfidence ?? undefined,
      requiresUserAction: dto.requiresUserAction ?? false,
      deadline: dto.deadline ?? undefined,
      completionCriteria: dto.completionCriteria ?? [],
      payloadSchema: dto.payloadSchema ?? undefined,
      enabled: dto.enabled ?? false,
      disabledReason: dto.disabledReason ?? undefined,
      kind: dto.kind ?? undefined,
      placement: dto.placement ?? undefined,
      requiresInput: dto.requiresInput ?? false,
      confirmationRequired: dto.confirmationRequired ?? false,
      expectedState: toOptionalExpectedState(dto.expectedState),
      refreshHints: dto.refreshHints ?? [],
      riskLevel: dto.riskLevel ?? undefined,
      executionMode: dto.executionMode ?? undefined,
      endpointTemplate: dto.endpointTemplate ?? undefined,
      httpMethod: toOptionalActionHttpMethod(dto.httpMethod),
      payloadTemplate: dto.payloadTemplate ?? undefined,
      inputSchema: toActionInputSchemaViewModel(dto.inputSchema)
    };
  }

export function toAvailableActionsViewModel(
    dto?: readonly HomeNextBestActionResponse[] | null
  ): readonly DecisionAction[] | undefined {
    if (!dto) {
      return undefined;
    }

    return dto
      .map((action) => toActionViewModel(action))
      .filter((action): action is DecisionAction => !!action);
  }

export function resolveActionKey(
    dto?: Pick<HomeNextBestActionCompactResponse, "actionKey" | "key"> | null
  ): string | null {
    return dto?.actionKey ?? dto?.key ?? null;
  }

export function resolveActionLabel(
    dto?: Pick<HomeNextBestActionCompactResponse, "label" | "displayName"> | null
  ): string | null {
    return dto?.label ?? dto?.displayName ?? null;
  }

export function toActionInputSchemaViewModel(
    dto?: HomeIaActionInputSchemaResponse | null
  ): HomeIaActionInputSchema | null {
    if (!dto?.type || dto.type !== "object") {
      return null;
    }

    const properties = Object.entries(dto.properties ?? {}).reduce<Record<string, HomeIaActionInputProperty>>(
      (accumulator, [key, value]) => {
        const property = toActionInputPropertyViewModel(value);
        if (property) {
          accumulator[key] = property;
        }
        return accumulator;
      },
      {}
    );

    return {
      type: "object",
      required: dto.required ?? [],
      properties
    };
  }

export function toActionInputPropertyViewModel(
    dto?: HomeIaActionInputPropertyResponse | null
  ): HomeIaActionInputProperty | null {
    if (!dto?.type || !dto.label) {
      return null;
    }

    return {
      type: dto.type,
      label: dto.label,
      required: dto.required ?? false,
      options: (dto.options ?? [])
        .filter((option): option is { value: string; label: string } => !!option?.value && !!option?.label)
        .map((option) => ({ value: option.value, label: option.label })),
      placeholder: dto.placeholder ?? undefined,
      helperText: dto.helperText ?? undefined
    };
  }
